import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivitiesCancelScenario,
  ActivitiesCancelScenarioEnum,
  Maybe,
  PollCancelQuoteSuccess,
  ScheduleCancelQuoteRequest,
  ResponseEnum,
  PollCancelQuoteRequest,
  ExperienceReservation,
  ScheduleCancelFulfillRequest,
  SelfServeEvents,
  ExperienceReservationDetails,
} from "redmond";
import { Box, Divider, Typography } from "@material-ui/core";
import {
  ActionButton,
  B2BSpinnerWithText,
  GenericModalContent,
  Icon,
  ImportantInfoList,
  IconName,
  twoDecimalFormatter,
} from "halifax";
import { RouteComponentProps } from "react-router-dom";
import dayjs from "dayjs";
import * as constants from "./constants";
import { getActivitiesCancelQuoteSchedule } from "../../../../../../../../api/v1/itinerary/getActivitiesCancelQuoteSchedule";
import { getActivitiesCancelQuotePoll } from "../../../../../../../../api/v1/itinerary/getActivitiesCancelQuotePoll";
import "./styles.scss";
import { CancelExperienceModalContentConnectorProps } from "./container";
import { getActivitiesCancelFulfillSchedule } from "../../../../../../../../api/v1/itinerary/getActivitiesCancelFulfillSchedule";
import { getActivitiesCancelFulfillPoll } from "../../../../../../../../api/v1/itinerary/getActivitiesCancelFulfillPoll";
import * as generalConstants from "../../constants";
import { trackEvent } from "../../../../../../../../api/v1/analytics/trackEvent";
import {
  useIsTravelApp,
  travelAppInterface,
  TravelAppInterfaceEventName,
} from "@capone/common";

export interface ICancelExperienceModalContentProps
  extends CancelExperienceModalContentConnectorProps,
    RouteComponentProps {
  experience: ExperienceReservation;
}

export enum CancelStep {
  LoadingPolicy,
  CancellationInfo,
  ConfirmCancellation,
  CancellationProcessing,
  CancellationSuccess,
  CancellationFailure,
}

interface ExperienceInfoProps {
  reservation: ExperienceReservationDetails;
}

const ExperienceInfo = ({ reservation }: ExperienceInfoProps) => {
  const {
    experienceDetails,
    experienceSelection: { travelDate, providerExperienceStartTime },
  } = reservation;
  if (!experienceDetails) return null;
  return (
    <Box className="experience-details-container">
      <Typography variant="body1" className="name">
        {experienceDetails?.name}
      </Typography>
      <Typography variant="body1" className="date-and-time">
        {dayjs(travelDate).format("ddd, MMM D")}
        {providerExperienceStartTime && (
          <>
            <span className="separator">|</span>
            {providerExperienceStartTime}
          </>
        )}
      </Typography>
    </Box>
  );
};

const CancelExperienceModalContent = (
  props: ICancelExperienceModalContentProps,
) => {
  const [cancelStep, setCancelStep] = useState<CancelStep>(
    CancelStep.LoadingPolicy,
  );
  const [cancelScenario, setCancelScenario] =
    useState<Maybe<ActivitiesCancelScenario>>(null);

  const [pollQuoteReq, setPollQuoteReq] =
    useState<Maybe<PollCancelQuoteRequest>>(null);

  const { isTravelApp } = useIsTravelApp();

  const {
    experience: { reservation },
    setOpenModal,
    // history,
  } = props;

  const cancelQuotePoll = async (quoteId: string, sessionId: string) => {
    let pollFailed = false;
    while (!pollFailed) {
      // eslint-disable-next-line no-await-in-loop
      const pollRes = await getActivitiesCancelQuotePoll({
        quoteId,
        sessionId,
      });

      switch (pollRes.PollCancelQuoteResponse) {
        case ResponseEnum.Success:
          switch (
            (pollRes as PollCancelQuoteSuccess).activitiesCancelScenario
              .ActivitiesCancelScenario
          ) {
            case ActivitiesCancelScenarioEnum.AlreadyCanceled:
            case ActivitiesCancelScenarioEnum.BookingPending:
            case ActivitiesCancelScenarioEnum.CancellationPending:
            case ActivitiesCancelScenarioEnum.ContactCustomerService:
            case ActivitiesCancelScenarioEnum.FullyRefundable:
            case ActivitiesCancelScenarioEnum.NonCancellable:
            case ActivitiesCancelScenarioEnum.NonRefundable:
            case ActivitiesCancelScenarioEnum.PartiallyRefundable:
            case ActivitiesCancelScenarioEnum.PastActivityStart:
              setCancelStep(CancelStep.CancellationInfo);
              break;
            default:
              setCancelStep(CancelStep.LoadingPolicy);
          }
          setCancelScenario(pollRes.activitiesCancelScenario);
          return;
        case ResponseEnum.Pending:
          break;
        case ResponseEnum.Failure:
        default:
          setCancelStep(CancelStep.CancellationFailure);
          pollFailed = true;
      }
    }
  };

  const getCancelInfo = async (
    customerReservationId: string,
    orderedProductId: string,
  ) => {
    const scheduleQuoteReq: ScheduleCancelQuoteRequest = {
      customerReservationId,
      orderedProductId,
    };
    setCancelStep(CancelStep.LoadingPolicy);
    const scheduleQuoteRes = await getActivitiesCancelQuoteSchedule(
      scheduleQuoteReq,
    );

    switch (scheduleQuoteRes.Response) {
      case ResponseEnum.Success: {
        const { quoteId, sessionId } = scheduleQuoteRes.value;
        setPollQuoteReq(scheduleQuoteRes.value);
        await cancelQuotePoll(quoteId, sessionId);
        break;
      }
      case ResponseEnum.Pending:
      case ResponseEnum.Failure:
      default:
        setCancelStep(CancelStep.CancellationFailure);
        break;
    }

    // Fire pageLoaded event when eligibility check completes
    if (isTravelApp) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.PAGE_LOADED,
      });
    }
  };

  const closeModal = useCallback(
    () =>
      setOpenModal({
        type: null,
        selectedItinerary: null,
      }),
    [setOpenModal],
  );

  const prepareCancellation = useCallback(() => {
    if (reservation.bookingSession?.cartOrderedProductId)
      getCancelInfo(
        reservation.customerReservationId,
        reservation.bookingSession?.cartOrderedProductId,
      );
  }, [reservation]);

  useEffect(() => {
    trackEvent({
      eventName: SelfServeEvents.ViewedCancelModal,
      properties: {
        product_funnel: "activity",
        ...reservation.trackingProperties,
      },
    });
    prepareCancellation();
  }, [prepareCancellation]);

  const cancelFulfillPoll = async (quoteId: string, sessionId: string) => {
    let pollFailed = false;

    const properties = {
      product_funnel: "activity",
      ...reservation.trackingProperties,
    };

    while (!pollFailed) {
      // eslint-disable-next-line no-await-in-loop
      const pollFulfillRes = await getActivitiesCancelFulfillPoll({
        quoteId,
        sessionId,
      });

      switch (pollFulfillRes.PollCancelFulfillResponse) {
        case ResponseEnum.Success: {
          setCancelStep(CancelStep.CancellationSuccess);
          trackEvent({
            eventName: SelfServeEvents.ResponseSuccess,
            properties,
          });
          return;
        }
        case ResponseEnum.Pending:
          break;
        case ResponseEnum.Failure:
        default: {
          setCancelStep(CancelStep.CancellationFailure);
          pollFailed = true;
          trackEvent({
            eventName: SelfServeEvents.ResponseFailure,
            properties,
          });
        }
      }
    }
  };

  const confirmCancellation = async (quoteId: string) => {
    const req: ScheduleCancelFulfillRequest = {
      quoteId,
      cancelReasonCode: "Customer_Service.I_canceled_my_entire_trip", // hardcoding this for MVP
    };
    setCancelStep(CancelStep.CancellationProcessing);
    const scheduleFulfillRes = await getActivitiesCancelFulfillSchedule(req);

    switch (scheduleFulfillRes.Response) {
      case ResponseEnum.Success: {
        const { quoteId: fulfillQuoteId, sessionId } = scheduleFulfillRes.value;
        setPollQuoteReq(scheduleFulfillRes.value);
        await cancelFulfillPoll(fulfillQuoteId, sessionId);
        break;
      }
      case ResponseEnum.Pending:
      case ResponseEnum.Failure:
      default:
        setCancelStep(CancelStep.CancellationFailure);
        trackEvent({
          eventName: SelfServeEvents.ResponseFailure,
          properties: {
            product_funnel: "activity",
            ...reservation.trackingProperties,
          },
        });
        break;
    }
  };

  const renderCancellationInfo = () => {
    const { cancelCopy } = cancelScenario || {};

    return (
      <Box className="cancellation-info-container">
        <Typography variant="h2" className="modal-title">
          {cancelCopy?.title}
        </Typography>
        <Box>
          <Typography variant="body2">{cancelCopy?.body}</Typography>
        </Box>
        <Divider className="modal-divider" />
        <ExperienceInfo reservation={reservation} />
        {cancelScenario && "refundSummary" in cancelScenario && (
          <>
            <Divider className="modal-divider" />
            <Box className="refund-amount">
              <Typography variant="body2">
                {constants.TOTAL_AMOUNT_REFUNDED}
              </Typography>
              <Typography className="amount">
                {
                  cancelScenario?.refundSummary.totalCompensation.fiat
                    .currencySymbol
                }
                {twoDecimalFormatter(
                  cancelScenario?.refundSummary.totalCompensation.fiat.value,
                )}
              </Typography>
            </Box>
          </>
        )}
        {(cancelCopy?.importantInfo?.length ?? 0) > 0 ? (
          <Box className="info-items-container">
            <Divider className="modal-divider" />
            <ImportantInfoList
              infoItems={cancelCopy?.importantInfo}
              title={constants.CANCEL_INFO_TITLE}
            />
          </Box>
        ) : undefined}
        <Divider className="modal-divider" />
        <ActionButton
          className="cancel-button"
          defaultStyle="h4r-primary"
          message={cancelCopy?.callToAction ?? "Cancel experience"}
          onClick={() => {
            trackEvent({
              eventName: SelfServeEvents.ClickCancel,
              properties: {
                product_funnel: "activity",
                ...reservation.trackingProperties,
              },
            });
            setCancelStep(CancelStep.ConfirmCancellation);
          }}
        />
      </Box>
    );
  };

  const renderConfirmCancellation = () => {
    if (cancelScenario && "cancelConfirmationCopy" in cancelScenario) {
      return (
        <Box className="confirm-cancellation-container">
          <Typography variant="h2" className="modal-title">
            {cancelScenario.cancelConfirmationCopy!.title}
          </Typography>
          {cancelScenario.cancelConfirmationCopy!.body.map((cancelInfo) => (
            <Typography variant="body2">{cancelInfo}</Typography>
          ))}

          <Divider className="modal-divider" />
          <ExperienceInfo reservation={reservation} />

          <Divider className="modal-divider" />
          <ActionButton
            className="confirm-cancel-button red"
            defaultStyle="h4r-primary"
            message={
              cancelScenario.cancelConfirmationCopy!.callToAction ??
              "Cancel experience"
            }
            onClick={() => {
              trackEvent({
                eventName: SelfServeEvents.ConfirmCancellation,
                properties: {
                  product_funnel: "activity",
                  ...reservation.trackingProperties,
                },
              });
              confirmCancellation(pollQuoteReq?.quoteId ?? "");
            }}
          />
        </Box>
      );
    }
    return null;
  };
  const renderLoading = useCallback(
    () => <B2BSpinnerWithText subtitle={constants.LOADING_POLICY} />,
    [],
  );

  const renderCancellationProcessing = useCallback(
    () => <B2BSpinnerWithText subtitle={constants.PROCESSING_CANCEL} />,
    [],
  );

  const renderCancellationSuccess = (closeCancellationModal: () => void) => (
    <GenericModalContent
      className="homes-cancel-modal-success"
      title={generalConstants.CANCELLATION_SUCCESS_TITLE}
      subtitle={generalConstants.CANCELLATION_SUCCESS_COPY}
      image={<Icon className="success-icon" name={IconName.Checked} />}
      actions={
        isTravelApp ? undefined : (
          <ActionButton
            defaultStyle="h4r-primary"
            message={constants.BACK_TO_TRIPS}
            onClick={closeCancellationModal}
          />
        )
      }
    />
  );

  const renderCancellationFailure = (closeCancellationModal: () => void) => (
    <Box className="cancellation-info-container">
      <Typography variant="h2" className="modal-title">
        {constants.SOMETHING_WENT_WRONG}
      </Typography>
      <Typography variant="body2">
        {constants.DEFAULT_CUSTOMER_SERVICE_CANCEL_BODY}
      </Typography>

      {isTravelApp ? undefined : (
        <>
          <Divider className="modal-divider" />
          <ActionButton
            className="confirm-cancel-button red"
            defaultStyle="h4r-primary"
            message={constants.BACK_TO_TRIPS}
            onClick={closeCancellationModal}
          />
        </>
      )}
    </Box>
  );

  const ModalContent = useMemo(() => {
    switch (cancelStep) {
      case CancelStep.LoadingPolicy:
        return renderLoading();
      case CancelStep.CancellationInfo:
        return renderCancellationInfo();
      case CancelStep.CancellationProcessing:
        return renderCancellationProcessing();
      case CancelStep.ConfirmCancellation:
        return renderConfirmCancellation();
      case CancelStep.CancellationSuccess:
        return renderCancellationSuccess(() => {
          closeModal();
          // [TODO]: Update to go to that reservation once tripId query params for experiences is set up
          // history.push(
          //   `${PATH_HOME}?tripsFilter=Past%20Trips&tripId=${home.reservation.id.value}`
          // );
          window.location.reload();
        });
      case CancelStep.CancellationFailure:
        return renderCancellationFailure(() => {
          closeModal();
          window.location.reload();
        });
      default:
        return null;
    }
  }, [cancelStep]);

  return <Box className="cancel-experience-modal-content">{ModalContent}</Box>;
};

export default CancelExperienceModalContent;
